import React, { useState } from 'react';
import { Certification } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { FormSelect } from '../common/FormSelect';

interface CertificationsSectionProps {
  certifications: Certification[];
  onUpdate: (certifications: Certification[]) => void;
}

interface CertificationFormProps {
  certification: Certification;
  onChange: (certification: Certification) => void;
  onRemove: () => void;
}

const CertificationForm: React.FC<CertificationFormProps> = ({ certification, onChange, onRemove }) => {
  const handleFieldChange = (field: keyof Certification) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    onChange({ ...certification, [field]: e.target.value });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 80; year--) {
      years.push(year.toString());
    }
    return years;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Certification Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <FormInput
        label="Certification Name"
        name="certification_name"
        value={certification.certification_name}
        onChange={handleFieldChange('certification_name')}
        maxLength={100}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormSelect
          label="Certification Date"
          name="certification_date"
          value={certification.certification_date}
          onChange={handleFieldChange('certification_date')}
          options={generateYearOptions()}
          placeholder="Select year"
        />
        <FormInput
          label="Organization"
          name="certification_organization"
          value={certification.certification_organization}
          onChange={handleFieldChange('certification_organization')}
          maxLength={100}
        />
      </div>
    </div>
  );
};

export const CertificationsSection: React.FC<CertificationsSectionProps> = ({
  certifications,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editCertifications, setEditCertifications] = useState<Certification[]>(certifications || []);

  const handleEdit = () => {
    setEditCertifications(certifications || []);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditCertifications(certifications || []);
    setIsEditing(false);
  };

  const handleSave = () => {
    const sortedCertifications = [...editCertifications].sort((a, b) => {
      const dateA = a.certification_date || '0000';
      const dateB = b.certification_date || '0000';
      return dateB.localeCompare(dateA);
    });
    onUpdate(sortedCertifications);
    setIsEditing(false);
  };

  const handleAddCertification = () => {
    setEditCertifications([
      ...editCertifications,
      {
        certification_name: '',
        certification_date: '',
        certification_organization: ''
      }
    ]);
  };

  const handleUpdateCertification = (index: number) => (cert: Certification) => {
    const updated = [...editCertifications];
    updated[index] = cert;
    setEditCertifications(updated);
  };

  const handleRemoveCertification = (index: number) => () => {
    setEditCertifications(editCertifications.filter((_, i) => i !== index));
  };

  const sortedViewCertifications = [...(certifications || [])].sort((a, b) => {
    const dateA = a.certification_date || '0000';
    const dateB = b.certification_date || '0000';
    return dateB.localeCompare(dateA);
  });

  return (
    <EditableSection
      title="Certifications"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editCertifications.map((cert, index) => (
            <CertificationForm
              key={index}
              certification={cert}
              onChange={handleUpdateCertification(index)}
              onRemove={handleRemoveCertification(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddCertification}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Certification
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedViewCertifications.length > 0 ? (
            sortedViewCertifications.map((cert, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{cert.certification_name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {cert.certification_organization}
                    </p>
                  </div>
                  {cert.certification_date && (
                    <span className="text-sm text-gray-500">{cert.certification_date}</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No certifications added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};